import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ReloadOutlined } from '@ant-design/icons';
import { Workflow } from 'lucide-react';

import { PageHeader, Tile } from '../../../components/shared';
import { Button, Skeleton } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { BadgeLoaiLuong, IconLoaiLuong, KhongCoQuyen, LoiRpa } from '../components/chung';
import { DanhSachWorkflow } from '../components/workflow/DanhSachWorkflow';
import { PanelWorkflow } from '../components/workflow/PanelWorkflow';
import type { TabWorkflow } from '../components/workflow/PanelWorkflow';
import { LOAI_LUONG, QUYEN } from '../constants';
import { useProjects, useWorkflows } from '../hooks/useOpenCore';
import { KHOA_RPA } from '../hooks/useRpa';
import { LOAI_WORKFLOW, demTheoLoai, ganProject, giaiWorkflowCon, workflowDangXem } from '../nghiep-vu/workflow';
import type { WorkflowCoProject } from '../nghiep-vu/workflow';

/**
 * Danh sách luồng (`/rpa/workflows`, `RpaWorkflowsView` của native): chọn Workflow có sẵn trên OpenCore để xem các
 * bước, biến & tham số và thông tin. Người có `rpa:workflow:update` sửa được giá trị mặc định; người có
 * `rpa:workflow:run` kết nối tài khoản OpenCore và bấm chạy.
 */
export function WorkflowView() {
  const { t } = useT();
  const qc = useQueryClient();
  const quyen = useModulePermissions(QUYEN);
  const workflows = useWorkflows(quyen.workflowDoc);
  const projects = useProjects(quyen.workflowDoc);
  const [daChon, setDaChon] = useState('');
  // "Xem thông tin" ở menu ⋮ mở panel ở tab Thông tin kể cả khi Workflow đó đang mở: `lan` đổi là panel dựng lại.
  const [moTab, setMoTab] = useState<{ tab: TabWorkflow; lan: number }>({ tab: 'info', lan: 0 });

  const items = useMemo(() => ganProject(workflows.data ?? [], projects.data ?? []), [workflows.data, projects.data]);
  const dangXem = workflowDangXem(items, daChon);
  const giaiCon = useCallback((target: string) => giaiWorkflowCon(target, items, projects.data ?? []), [items, projects.data]);

  const tieuDe = t('nav.muc.danhSachLuong');
  const lamMoi = () => {
    void qc.invalidateQueries({ queryKey: KHOA_RPA.workflows });
    void qc.invalidateQueries({ queryKey: KHOA_RPA.projects });
    if (dangXem) void qc.invalidateQueries({ queryKey: KHOA_RPA.soDo(dangXem) });
  };
  const dangTai = workflows.isPending || projects.isPending;
  const loi = workflows.error ?? projects.error;

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: t('nav.muc.quanLyProjectWorkflow') }, { label: tieuDe }]}
        titleIcon={<Workflow size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaWorkflow.moTa')}
        actions={
          quyen.workflowDoc ? (
            <Button
              variant="outline"
              size="md"
              icon={<ReloadOutlined />}
              loading={!dangTai && (workflows.isFetching || projects.isFetching)}
              onClick={lamMoi}
            >
              {t('chung.lamMoi')}
            </Button>
          ) : undefined
        }
      />

      {!quyen.workflowDoc ? (
        <KhongCoQuyen quyen={QUYEN.workflowDoc} />
      ) : dangTai ? (
        <div className="space-y-4" aria-busy="true">
          <div className="grid gap-3 md:grid-cols-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      ) : loi ? (
        <Tile>
          <LoiRpa
            loi={loi}
            onThuLai={() => {
              void workflows.refetch();
              void projects.refetch();
            }}
          />
        </Tile>
      ) : (
        <div className="min-w-0 space-y-4">
          <TheTong items={items} />
          <div className="grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
            <Tile span="col-span-1" className="min-w-0 overflow-hidden p-3 lg:sticky lg:top-4">
              <div className="h-[360px] lg:h-[calc(100dvh-23rem)] lg:min-h-[400px]">
                <DanhSachWorkflow
                  items={items}
                  projects={projects.data ?? []}
                  dangChon={dangXem}
                  onChon={setDaChon}
                  onXemThongTin={(id) => {
                    setDaChon(id);
                    setMoTab((cu) => ({ tab: 'info', lan: cu.lan + 1 }));
                  }}
                />
              </div>
            </Tile>
            <Tile span="col-span-1" className="min-w-0 overflow-hidden p-3 sm:p-4">
              <PanelWorkflow
                key={`${dangXem}-${moTab.lan}`}
                workflowId={dangXem}
                tabMoSan={moTab.tab}
                suaDuoc={quyen.workflowSua}
                chayDuoc={quyen.workflowChay}
                giaiCon={giaiCon}
                onMoCon={setDaChon}
              />
            </Tile>
          </div>
        </div>
      )}
    </div>
  );
}

/** Hai thẻ tổng theo loại luồng (`WorkflowSummary` của native). */
function TheTong({ items }: { items: WorkflowCoProject[] }) {
  const { t, locale } = useT();
  const dem = demTheoLoai(items);
  return (
    <div className="grid min-w-0 gap-3 md:grid-cols-2">
      {LOAI_WORKFLOW.map((loai) => {
        const l = LOAI_LUONG[loai];
        return (
          <div key={loai} className={cn('flex min-w-0 flex-wrap items-center gap-3 rounded-lg border bg-surface px-3 py-2.5 sm:flex-nowrap sm:px-4', l.vien)}>
            <span className={cn('flex size-10 shrink-0 items-center justify-center rounded text-xl', l.nen, l.chu)}>
              <IconLoaiLuong loai={loai} />
            </span>
            <div className="min-w-0 flex-1 basis-40">
              <h2 className="text-sm font-semibold text-content-primary">{t(l.tieuDe)}</h2>
              <p className="mt-1 line-clamp-2 text-xs text-content-secondary">{t(l.moTa)}</p>
            </div>
            <div className="flex w-full shrink-0 items-center justify-between gap-1.5 sm:w-auto sm:flex-col sm:items-end">
              <BadgeLoaiLuong loai={loai} />
              <p className="flex items-baseline gap-2">
                <strong className={cn('text-xl tabular-nums', l.chu)}>{soVN(dem[loai], locale)}</strong>
                <span className="text-2xs text-content-muted">{t('rpaWorkflow.dangDung')}</span>
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
